import { useState, useEffect, useCallback } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { PageContainer, DataTable, PremiumModal, Button } from "components/ui";

const leaveTypeLabel = {
  PAID: "Paid leave",
  SICK: "Sick leave",
  UNPAID: "Unpaid",
  MATERNITY: "Maternity",
  OTHER: "Other",
};

const statusBadge = (status) => {
  const map = {
    PENDING:  { bg: "rgba(245,158,11,0.15)",   color: "#f59e0b"  },
    APPROVED: { bg: "rgba(16,185,129,0.15)",   color: "#10b981"  },
    REJECTED: { bg: "rgba(239,68,68,0.15)",    color: "#ef4444"  },
    CANCELLED:{ bg: "rgba(148,163,184,0.15)",  color: "#94a3b8"  },
  };
  const s = map[status] || map.CANCELLED;
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: "2px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
    }}>
      {status}
    </span>
  );
};

const LeaveApprovals = () => {
  const axiosPrivate = useAxiosPrivate();
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Review modal state
  const [modalOpen, setModalOpen]   = useState(false);
  const [selected, setSelected]     = useState(null);   // the LeaveRequestRecord being reviewed
  const [comment, setComment]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionType, setActionType] = useState(null);   // "approve" | "reject"

  const size = 12;

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPrivate.get(
        `/api/rh/leave-requests/pending?page=${page}&size=${size}`
      );
      setEntries(res.data.content);
      setTotalPages(res.data.page.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openReview = (entry, type) => {
    setSelected(entry);
    setActionType(type);
    setComment("");
    setModalOpen(true);
  };

  const submitReview = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await axiosPrivate.post(
        `/api/rh/leave-requests/${selected.id}/${actionType}`,
        { comment }
      );
      setModalOpen(false);
      fetchEntries();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const dayCount = (start, end) => {
    const d = (new Date(end) - new Date(start)) / 86_400_000;
    return `${d + 1}d`;
  };

  return (
    <PageContainer>
      <DataTable
        title="Leave requests"
        subtitle="Pending approvals for your branch"
        loading={loading}
        data={entries}
        rowKey={(e) => e.id}
        search={true}
        searchPlaceholder="Search by name or reference…"
        paginate={false}
        emptyTitle="No pending requests"
        emptySubtitle="All leave requests have been reviewed."
        columns={[
          {
            key: "employee",
            header: "Employee",
            render: (e) => (
              <div>
                <div style={{ fontWeight: 500 }}>{e.employeeName}</div>
                <div className="t-mono t-muted" style={{ fontSize: 11 }}>
                  {leaveTypeLabel[e.leaveType]}
                </div>
              </div>
            ),
          },
          {
            key: "dates",
            header: "Period",
            render: (e) => (
              <div>
                <span className="t-mono">{e.startDate}</span>
                <span style={{ color: "var(--text-muted)", margin: "0 6px" }}>→</span>
                <span className="t-mono">{e.endDate}</span>
                <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-muted)" }}>
                  ({dayCount(e.startDate, e.endDate)})
                </span>
              </div>
            ),
          },
          {
            key: "motif",
            header: "Reason",
            render: (e) => (
              <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
                {e.motif || "—"}
              </span>
            ),
          },
          { key: "status", header: "Status", render: (e) => statusBadge(e.status) },
          {
            key: "actions",
            header: "",
            align: "right",
            render: (e) =>
              e.status === "PENDING" ? (
                <div style={{ display: "inline-flex", gap: 6 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    style={{ color: "#10b981" }}
                    onClick={() => openReview(e, "approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    style={{ color: "#ef4444" }}
                    onClick={() => openReview(e, "reject")}
                  >
                    Reject
                  </Button>
                </div>
              ) : null,
          },
        ]}
        footer={
          totalPages > 1 ? (
            <div className="dt-foot">
              <span>
                Page <strong>{page + 1}</strong> of {totalPages}
              </span>
              <div style={{ display: "inline-flex", gap: 6 }}>
                <Button
                  variant="ghost" size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ‹ Prev
                </Button>
                <Button
                  variant="ghost" size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Next ›
                </Button>
              </div>
            </div>
          ) : null
        }
      />

      {/* Review modal */}
      <PremiumModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        title={actionType === "approve" ? "Approve request" : "Reject request"}
        size="md"
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={actionType === "approve" ? "primary" : "danger"}
              onClick={submitReview}
              disabled={submitting}
            >
              {submitting
                ? "Saving…"
                : actionType === "approve"
                ? "Confirm approval"
                : "Confirm rejection"}
            </Button>
          </div>
        }
      >
        {selected && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Summary */}
            <div style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 10, padding: "14px 16px",
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                {selected.employeeName}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 16 }}>
                <span>{leaveTypeLabel[selected.leaveType]}</span>
                <span className="t-mono">{selected.startDate} → {selected.endDate}</span>
                <span>({dayCount(selected.startDate, selected.endDate)})</span>
              </div>
              {selected.motif && (
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
                  "{selected.motif}"
                </div>
              )}
            </div>

            {/* Comment */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>
                Comment <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="Add a note for the employee…"
                style={{
                  width: "100%", borderRadius: 8,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "inherit", padding: "10px 12px",
                  fontSize: 13, resize: "vertical",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        )}
      </PremiumModal>
    </PageContainer>
  );
};

export default LeaveApprovals;